import type React from "react";
import { useGlobalState } from "senf-shared";
import AccordionListItem, {
  type AccordionListItemAction,
  type AccordionListItemData,
  type AccordionListItemGroup,
  type AccordionListItemGroupAction,
} from "../../../../core/accordionListItem/AccordionListItem";
import { removeAssetById, zoomToAssetById } from "./cesium/cesium";

export interface SavedAsset extends AccordionListItemData {
  assetId: number;
  assetName: string;
  customAssetName?: string | null;
  detectedEpsg: string;
  fileSize: string;
  uploadDate: Date;
}

interface SavedModelsProps {
  assets: SavedAsset[];
  onRemove: (assetId: number) => void;
}

// Helper function to extract group name from asset name or custom name
const getAssetGroupName = (asset: SavedAsset): string => {
  // If custom asset name exists, use it as group name
  if (asset.customAssetName) {
    return asset.customAssetName;
  }

  // Extract group name from asset name (e.g., "VP_2000_3TM_KIB_DL_027" from "VP_2000_3TM_KIB_DL_027_AA_002-C_Teilmodell Lugaer Graben")
  const nameParts = asset.assetName.split("_");
  if (nameParts.length >= 4) {
    return nameParts.slice(0, 4).join("_"); // Take first 4 parts as group identifier
  }

  // Fallback to first part of the name
  return nameParts[0] || "Other";
};

// Group assets by their group name
const groupAssets = (assets: SavedAsset[]): Record<string, SavedAsset[]> => {
  return assets.reduce(
    (groups, asset) => {
      const groupName = getAssetGroupName(asset);
      if (!groups[groupName]) {
        groups[groupName] = [];
      }
      groups[groupName].push(asset);
      return groups;
    },
    {} as Record<string, SavedAsset[]>,
  );
};

const SavedModels: React.FC<SavedModelsProps> = ({ assets, onRemove }) => {
  const [statefulCesiumMap] = useGlobalState().statefulCesiumMap;
  const viewer = statefulCesiumMap?.viewer;

  if (!assets || assets.length === 0) {
    return null;
  }

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const handleAssetClick = (asset: SavedAsset) => {
    zoomToAssetById(asset.assetId);
  };

  const handleDeleteGroup = (groupAssets: SavedAsset[]) => {
    groupAssets.forEach((asset) => {
      // Remove from Cesium map first
      if (viewer) {
        removeAssetById(asset.assetId, viewer);
      }
      // Then call the parent's onRemove callback
      onRemove(asset.assetId);
    });
  };

  const handleDeleteAsset = (asset: SavedAsset) => {
    // Remove from Cesium map first
    if (viewer) {
      removeAssetById(asset.assetId, viewer);
    }
    // Then call the parent's onRemove callback
    onRemove(asset.assetId);
  };

  // Transform assets into groups for AccordionListItem
  const groupedAssets = groupAssets(assets);
  const groups: AccordionListItemGroup<SavedAsset>[] = Object.entries(
    groupedAssets,
  ).map(([groupName, groupAssets]) => ({
    groupKey: groupName,
    title: groupName,
    subtitle: `${groupAssets.length} model${groupAssets.length !== 1 ? "s" : ""}`,
    items: groupAssets.map((asset) => ({ ...asset, id: asset.assetId })),
    actions: [
      {
        text:
          groupAssets.length > 1
            ? `Delete All (${groupAssets.length})`
            : "Delete",
        leadingIcon: "trash",
        critical: true,
        onClick: () => handleDeleteGroup(groupAssets),
      },
    ] as AccordionListItemGroupAction[],
  }));

  const getItemPrimaryContent = (asset: SavedAsset) => asset.assetName;

  const getItemSecondaryContent = (asset: SavedAsset) =>
    `${asset.detectedEpsg} • ${asset.fileSize} • ${formatDate(asset.uploadDate)}`;

  const getItemActions = (asset: SavedAsset): AccordionListItemAction[] => [
    {
      text: "Delete",
      leadingIcon: "trash",
      critical: true,
      onClick: () => handleDeleteAsset(asset),
    },
  ];

  return (
    <AccordionListItem
      groups={groups}
      onItemClick={handleAssetClick}
      getItemPrimaryContent={getItemPrimaryContent}
      getItemSecondaryContent={getItemSecondaryContent}
      getItemActions={getItemActions}
    />
  );
};

export default SavedModels;
